// src/components/modals/EditInventoryModal.jsx
import { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import RawMaterialInventoryService from '../../services/rawMaterialInventoryService'
import AuthService from '../../services/authService'
import { RawMaterialInventoryModel } from '../../utils/rawMaterialInventoryModel'

const EditInventoryModal = ({ isOpen, onClose, inventory, onInventoryUpdated }) => {
  const currentUser = AuthService.getCurrentUser()
  const isSuperAdmin =
    currentUser?.role?.toLowerCase() === 'super_admin' ||
    currentUser?.role?.toLowerCase() === 'superadmin'

  const [formData, setFormData] = useState({
    currentStock: '',
    unitPrice: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Initialize form with inventory data
  useEffect(() => {
    if (isOpen && inventory) {
      setFormData({
        currentStock: inventory.currentStock?.toString() || '0',
        unitPrice: inventory.unitPrice?.toString() || '',
        isActive: inventory.isActive !== undefined ? inventory.isActive : true
      })
      setErrors({})
    }
  }, [isOpen, inventory])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate form using the model
  const validateForm = () => {
    const validation = RawMaterialInventoryModel.validatePartial(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for API - only send changed fields
      const updateData = {}
      
      if (isSuperAdmin && formData.currentStock !== '' && Number(formData.currentStock) !== (inventory.currentStock ?? 0)) {
        updateData.currentStock = Number(formData.currentStock)
      }
      
      if (formData.unitPrice !== '' && Number(formData.unitPrice) !== (inventory.unitPrice || 0)) {
        updateData.unitPrice = Number(formData.unitPrice)
      }
      
      if (formData.isActive !== inventory.isActive) {
        updateData.isActive = formData.isActive
      }
      
      // Only proceed if there are changes
      if (Object.keys(updateData).length === 0) {
        console.log('ℹ️ No changes detected')
        onClose()
        return
      }
      
      console.log('📤 Updating inventory:', {
        inventoryId: inventory._id,
        updateData
      })
      
      const response = await RawMaterialInventoryService.updateInventory(inventory._id, updateData)
      
      if (response.success) {
        console.log('✅ Inventory updated successfully:', response)
        
        // Reset form
        setFormData({
          currentStock: '',
          unitPrice: '',
          isActive: true
        })
        setErrors({})
        
        // Notify parent component with updated data
        const updatedInventory = response.data?.inventory || { ...inventory, ...updateData }
        onInventoryUpdated(updatedInventory)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        setErrors({ general: response.message || 'Failed to update inventory' })
      }
    } catch (error) {
      console.error('❌ Error updating inventory:', error)
      
      let errorMessage = 'Failed to update inventory. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Inventory not found. It may have been deleted by another user.'
      } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your input and try again.'
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }
      
      setErrors({
        general: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Don't render if not open or no inventory
  if (!isOpen || !inventory) return null

  const material = inventory.rawMaterial
  const branch = inventory.branch
  const unit = material?.unit

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-xl font-semibold text-gray-900">Edit Inventory</h2>
              {isSuperAdmin && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                  Super Admin
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {isSuperAdmin ? 'Authorized to adjust current stock directly' : 'View or update inventory properties'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Material Info - Read Only */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Material Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Material:</span>
                <span className="font-medium">{material?.name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Code:</span>
                <span className="font-mono">{material?.code || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Branch:</span>
                <span className="font-medium">{branch?.name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Unit:</span>
                <span>{unit?.name || 'Unknown'} ({unit?.abbreviation || 'N/A'})</span>
              </div>
              {material?.threshold && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Threshold:</span>
                  <span>{material.threshold} {unit?.abbreviation || ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Current Stock */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Current Stock *
              </label>
              {isSuperAdmin ? (
                <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                  Super Admin Editable
                </span>
              ) : (
                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                  Super Admin Only
                </span>
              )}
            </div>
            <div className="relative">
              <Input
                name="currentStock"
                type="number"
                placeholder="e.g. 100"
                value={formData.currentStock}
                onChange={handleInputChange}
                className={errors.currentStock ? 'border-red-300 focus:border-red-500' : ''}
                disabled={loading || !isSuperAdmin}
                step="any"
                required
              />
              {unit && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-sm text-gray-500">{unit.abbreviation}</span>
                </div>
              )}
            </div>
            {errors.currentStock && (
              <p className="text-red-600 text-sm mt-1">{errors.currentStock}</p>
            )}
            {!isSuperAdmin && (
              <p className="text-xs text-amber-600 mt-1">
                Only users with the Super Admin role can edit current stock.
              </p>
            )}
          </div>

          {/* Unit Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit Price (Rs)
            </label>
            <div className="relative">
              <Input
                name="unitPrice"
                type="number"
                placeholder="e.g. 25.50"
                value={formData.unitPrice}
                onChange={handleInputChange}
                className={errors.unitPrice ? 'border-red-300 focus:border-red-500' : ''}
                disabled={loading}
                min="0"
                step="0.01"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span className="text-sm text-gray-500">Rs</span>
              </div>
            </div>
            {errors.unitPrice && (
              <p className="text-red-600 text-sm mt-1">{errors.unitPrice}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Optional: Price per unit for inventory valuation
            </p>
          </div>

          {/* Stock Value Calculation */}
          {formData.currentStock && formData.unitPrice && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-900">Total Stock Value:</span>
                <span className="text-lg font-bold text-blue-900">
                  Rs. {(Number(formData.currentStock) * Number(formData.unitPrice)).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={handleStatusToggle}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              {/* <span className="ml-2 text-sm text-gray-600">
                {formData.isActive ? 'Active' : 'Inactive'}
              </span> */}
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditInventoryModal